import { useCallback, useEffect, useState } from 'react'
import { useStompClient, useSubscription } from 'react-stomp-hooks'
import { ChatMessage } from '../../models/chat'
import ChatService from '../../services/ChatService'
import CustomInput from '../../components/TextInput'
import CustomIconButton from '../../components/IconButton'
import SendIcon from '@mui/icons-material/Send'
import dayjs from 'dayjs'
import { scrollToChildInsideParent, sortDates } from '../../helpers/utils'
import { UserDTO } from '../../models/users'
import { CircularProgress, Divider } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { AppRoutes } from '../../helpers/routes'

interface ChatProps {
    thisUserId: string
    recipient: UserDTO
    updatePage: () => void
}
const Chat = ({ thisUserId, recipient, updatePage }: ChatProps) => {
    const [currentMessage, setCurrentMesage] = useState<string>('')
    const [messages, setMessages] = useState<ChatMessage[]>()
    const client = useStompClient()
    const navigate = useNavigate()

    useEffect(() => {
        scrollToChildInsideParent('messageList', 'lastMessage')
    }, [messages, updatePage])

    useEffect(() => {
        new ChatService().getChat(thisUserId, recipient.id).then((response) => {
            if (response.status !== 200) setMessages([])
            else setMessages(response.data ?? [])
        })
    }, [recipient.id, thisUserId])

    const onMessageReceived = (message: ChatMessage) => {
        if (message.senderId === recipient.id) {
            setMessages((prev) => [...prev!, message])
        }
        updatePage()
    }
    useSubscription(`/user/${thisUserId}/queue/messages`, (payload) =>
        onMessageReceived(JSON.parse(payload.body)),
    )

    const sendMessage = useCallback(() => {
        if (!currentMessage || currentMessage === '' || !client) return

        const newMessage = {
            senderId: thisUserId,
            recipientId: recipient.id,
            content: currentMessage.trim(),
            timestamp: dayjs().format('YYYY-MM-DD[T]HH:mm:ss'),
        } as ChatMessage
        setCurrentMesage('')

        new ChatService().sendMessage(newMessage).then((response) => {
            client.publish({
                destination: '/app/chat',
                body: JSON.stringify(newMessage),
            })
            setMessages((prev) => [...prev!, newMessage])
            updatePage()
        })
    }, [client, currentMessage, recipient.id, thisUserId, updatePage])

    const enterPress = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault()
                sendMessage()
            }
        },
        [sendMessage],
    )

    useEffect(() => {
        window.addEventListener('keydown', enterPress)
        return () => window.removeEventListener('keydown', enterPress)
    }, [enterPress])

    const MessageBubble = ({ message }: { message: ChatMessage }) => {
        const extraStyles = message.senderId === thisUserId ? 'bg-optional ml-auto' : 'bg-disabled'
        return (
            <>
                {message.recipientId === 'aux' ? (
                    <div className="flex items-center justify-center gap-3 my-3">
                        <Divider className="flex w-[35%] md:w-[46%]" />
                        <span className="text-xs text-gray">{message.timestamp}</span>
                        <Divider className="flex w-[35%] md:w-[46%]" />
                    </div>
                ) : (
                    <div
                        className={`w-fit max-w-[100%] md:max-w-[47%] break-words ${extraStyles} rounded-[20px] py-2 px-3 flex gap-2 items-end text-sm md:text-base`}
                    >
                        {message.content}
                        <span className="text-[13px] text-gray">
                            {dayjs(message.timestamp).format('HH:mm')}
                        </span>
                    </div>
                )}
            </>
        )
    }

    const addDateDividers = (messages: ChatMessage[]) => {
        const result = [] as ChatMessage[]
        messages.forEach((message) => {
            const lastMessageDate =
                result.length > 0 ? dayjs(result.slice(-1)[0].timestamp) : undefined
            const isDifferentDate =
                !lastMessageDate ||
                lastMessageDate.format('DD/MM/YYYY') !==
                    dayjs(message.timestamp).format('DD/MM/YYYY')

            if (isDifferentDate)
                result.push({
                    timestamp: dayjs(message.timestamp).format('DD/MM/YYYY'),
                    content: 'aux',
                    senderId: 'aux',
                    recipientId: 'aux',
                })
            result.push(message)
        })
        return result
    }

    return (
        <div className="h-full w-[75%] flex flex-col">
            <div className="w-full">
                <div className="flex gap-2 md:gap-4 px-3 md:px-6 py-2 md:py-4 items-center">
                    <img
                        src={!!recipient.picture ? recipient.picture : '/adminPhoto.png'}
                        alt="chatter"
                        className="h-10 md:h-12 w-10 md:w-12 rounded-[100%] outline outline-primary outline-1"
                    />
                    <span
                        className="text-base md:text-lg cursor-pointer hover:underline"
                        onClick={() => navigate(AppRoutes.user.replace(':userId', recipient.id))}
                    >
                        {recipient.firstName + ' ' + recipient.lastName}
                    </span>
                </div>
                <Divider />
            </div>
            <div
                className="h-full pt-1 md:pt-3 px-2 md:px-4 pr-1 overflow-y-auto"
                id="messageList"
            >
                <div className="flex flex-col gap-2 md:gap-4">
                    <div className="flex flex-col gap-[6px] overflow-y-auto px-2 md:px-4">
                        {messages ? (
                            addDateDividers(
                                messages.sort((x, y) =>
                                    sortDates(new Date(y.timestamp), new Date(x.timestamp)),
                                ),
                            ).map((x, index) => (
                                <div key={'message' + index.toString()}>
                                    <MessageBubble message={x} />
                                </div>
                            ))
                        ) : (
                            <div className="flex w-full justify-center">
                                <CircularProgress
                                    className="m-2 md:m-5 text-primary"
                                    size={64}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div id="lastMessage" />
            </div>
            <div className="flex p-2 md:p-4 md:pl-5 md:pr-2 gap-1 md:gap-2">
                <CustomInput
                    placeholder="Ingresá un mensaje"
                    className="w-full"
                    value={currentMessage}
                    onChange={(e) => setCurrentMesage(e.target.value)}
                    multiline
                />
                <CustomIconButton
                    icon={<SendIcon />}
                    className="px-2 md:px-4"
                    onClick={sendMessage}
                />
            </div>
        </div>
    )
}

export default Chat
